import { useState } from 'react';
import { MessageCircle, X } from 'lucide-react';

/**
 * WhatsAppFloatingButton — botón flotante "Habla con Vía"
 *
 * Aparece en esquina inferior derecha. Al hacer clic:
 *  - Despliega una pequeña burbuja con CTA
 *  - Al pulsar la burbuja, abre WhatsApp con mensaje pre-cargado
 *
 * Si no hay número configurado (REACT_APP_WHATSAPP_BOT_NUMBER vacío)
 * el componente NO se renderiza.
 */
function WhatsAppFloatingButton({
  message = '¡Hola Vía! Necesito ayuda con VíaDirecta.',
  variant = 'default',
}) {
  const [expanded, setExpanded] = useState(false);
  const number = (process.env.REACT_APP_WHATSAPP_BOT_NUMBER || '').trim();

  if (!number) return null;

  const link = `https://wa.me/${number}?text=${encodeURIComponent(message)}`;

  const positionClass =
    variant === 'inline'
      ? ''
      : 'fixed bottom-24 right-4 z-40 md:bottom-6 md:right-6';

  return (
    <div className={positionClass} data-testid="whatsapp-floating-button-wrapper">
      {/* Speech bubble */}
      {expanded && (
        <div
          className="absolute bottom-20 right-0 w-72 bg-white rounded-2xl shadow-2xl border border-emerald-100 overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-200"
          data-testid="whatsapp-bot-bubble"
        >
          <div className="bg-gradient-to-r from-emerald-500 to-green-600 px-4 py-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center text-xl">
              👋
            </div>
            <div className="flex-1">
              <p className="text-white font-semibold leading-tight">Vía</p>
              <p className="text-emerald-50 text-xs leading-tight">Asistente VíaDirecta · En línea</p>
            </div>
            <button
              onClick={(e) => { e.stopPropagation(); setExpanded(false); }}
              className="text-white/80 hover:text-white"
              aria-label="Cerrar"
              data-testid="whatsapp-bot-bubble-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-4 space-y-3">
            <div className="bg-slate-50 rounded-2xl rounded-tl-sm px-3 py-2 text-sm text-slate-700">
              ¡Hola! 👋 Soy <strong>Vía</strong>, tu asistente de VíaDirecta.
              <br />¿En qué puedo ayudarte hoy?
            </div>
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700 text-white text-center font-semibold py-3 rounded-xl shadow-md transition transform hover:scale-[1.02]"
              data-testid="whatsapp-bot-open"
              onClick={() => setExpanded(false)}
            >
              💬 Abrir WhatsApp
            </a>
            <p className="text-[10px] text-slate-400 text-center">
              Respuesta inmediata · 24/7
            </p>
          </div>
        </div>
      )}

      {/* Floating Action Button */}
      <button
        onClick={() => setExpanded((v) => !v)}
        aria-label="Habla con Vía"
        className="relative group w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 shadow-2xl shadow-emerald-500/40 flex items-center justify-center text-white transition transform hover:scale-110 active:scale-95"
        data-testid="whatsapp-floating-button"
      >
        {/* Pulsing ring */}
        <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-40 animate-ping" />
        <span className="absolute inset-0 rounded-full bg-emerald-500" />
        <MessageCircle className="relative w-7 h-7" strokeWidth={2.2} />

        {/* "Vía" pill on hover (desktop only) */}
        <span className="hidden md:block absolute right-full mr-3 px-3 py-1.5 bg-slate-900 text-white text-xs font-medium rounded-lg opacity-0 group-hover:opacity-100 transition pointer-events-none whitespace-nowrap shadow-lg">
          💬 Habla con Vía
        </span>
      </button>
    </div>
  );
}

export default WhatsAppFloatingButton;
export { WhatsAppFloatingButton };
